import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {invalidateEquipmentMoves} from '../project-deployed-equipment/invalidate-equipment-moves'
import {projectKeys} from '../projects/query-keys'
import type {CreateProjectBillingTermsInput} from '../projects/schemas'
import {
    applyServiceScheduleChange,
    createServiceSchedule,
    getServiceSchedule,
    listServiceSchedules,
    previewServiceScheduleChange,
    restoreScheduleOccurrence,
    reviseServiceScheduleBillingTerms,
    skipScheduleOccurrence,
} from './api'
import {serviceScheduleKeys} from './query-keys'
import type {CreateServiceScheduleInput, ServiceScheduleChangeInput} from './schemas'

type UseServiceSchedulesOptions = {
    enabled?: boolean
}

export function useServiceSchedules(projectId: string, options?: UseServiceSchedulesOptions) {
    return useQuery({
        queryKey: serviceScheduleKeys.list(projectId),
        queryFn: () => listServiceSchedules(projectId),
        enabled: (options?.enabled ?? true) && Boolean(projectId),
    })
}

export function useServiceSchedule(
    scheduleId: string | null,
    options?: UseServiceSchedulesOptions,
) {
    return useQuery({
        queryKey: serviceScheduleKeys.detail(scheduleId ?? ''),
        queryFn: () => getServiceSchedule(scheduleId!),
        enabled: (options?.enabled ?? true) && scheduleId != null,
    })
}

export function useCreateServiceSchedule() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateServiceScheduleInput) => createServiceSchedule(input),
        onSuccess: (schedule) => {
            void queryClient.invalidateQueries({
                queryKey: serviceScheduleKeys.list(schedule.projectId),
            })
            queryClient.setQueryData(serviceScheduleKeys.detail(schedule.id), schedule)
        },
    })
}

export function usePreviewServiceScheduleChange(scheduleId: string) {
    return useMutation({
        mutationFn: (input: ServiceScheduleChangeInput) =>
            previewServiceScheduleChange(scheduleId, input),
    })
}

export function useApplyServiceScheduleChange(scheduleId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: ServiceScheduleChangeInput) =>
            applyServiceScheduleChange(scheduleId, input),
        onSuccess: (schedule) => {
            queryClient.setQueryData(serviceScheduleKeys.detail(schedule.id), schedule)
            void queryClient.invalidateQueries({
                queryKey: serviceScheduleKeys.list(schedule.projectId),
            })
            void invalidateEquipmentMoves(queryClient, schedule.projectId)
        },
    })
}

/** Stamps a new terms version onto the schedule and its jobs that have no charges yet. */
export function useReviseServiceScheduleBillingTerms(scheduleId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateProjectBillingTermsInput) =>
            reviseServiceScheduleBillingTerms(scheduleId, input),
        onSuccess: (schedule) => {
            queryClient.setQueryData(serviceScheduleKeys.detail(schedule.id), schedule)
            void queryClient.invalidateQueries({
                queryKey: serviceScheduleKeys.list(schedule.projectId),
            })
            void queryClient.invalidateQueries({
                queryKey: projectKeys.detail(schedule.projectId),
            })
            void queryClient.invalidateQueries({
                queryKey: projectKeys.billingTerms(schedule.projectId),
            })
        },
    })
}

export function useSkipScheduleOccurrence(scheduleId: string, projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (jobId: string) => skipScheduleOccurrence(scheduleId, jobId),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: serviceScheduleKeys.detail(scheduleId)})
            void invalidateEquipmentMoves(queryClient, projectId)
        },
    })
}

export function useRestoreScheduleOccurrence(scheduleId: string, projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (jobId: string) => restoreScheduleOccurrence(scheduleId, jobId),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: serviceScheduleKeys.detail(scheduleId)})
            void invalidateEquipmentMoves(queryClient, projectId)
        },
    })
}
